import { NodeCell, ElementPropertyConfig, FarrisPropConfigWithData } from "@edp-pmf/grapheditor";
import { Signal, EventDefinition, BPMNShape } from "@edp-pmf/bpmn-model";
import { MxGraphNS, mxResources } from "@edp-pmf/mxgraph-ts";
import { IntermediateCatchEventImpl, Component, BackWardRule, ActualParameter, VariableSetting, SignalEventDefinitionImpl } from "@gsp-wf/wf-process-model";
import { PropertyHelper } from "./PropertyHelper";
import { BpmnModelHelper, FlowNodeState } from "@edp-pmf/bpmn-model";
import { WfConstants } from "../WfConstants";
import { IntermediateCatchEventImplPropertyKeys } from "./property-keys/IntermediateCatchEventImplPropertyKeys";

/**
 * 信号捕获活动节点
 */
export class IntermediateCatchEventImplCell extends NodeCell {
    clrTypeId: string = IntermediateCatchEventImplCell.CLR_TYPE_ID;
    name: string = "信号捕获";
    nameLanguage: { [lang: string]: string; } = {
      "zh-CHS": "信号捕获",
      "zh-CHT": "訊號捕獲",
      "en": "Acquisition",
  };
    static CLR_TYPE_ID: string = IntermediateCatchEventImpl.Clr_Type_ID;
    flowElement: IntermediateCatchEventImpl;

    imgUrls = {
        [FlowNodeState.Default]: WfConstants.IMAGE_PATH + "/catchEvent.png",
        [FlowNodeState.Selected]: WfConstants.IMAGE_PATH + "/catchEvent_select.svg",
    };

    constructor(value?: any, geometry?: MxGraphNS.mxGeometry, style?: string) {
        super(value, geometry, style);
    }

    createFlowElement(flowElt?: IntermediateCatchEventImpl) {
        const flowNode = super.createFlowElement(flowElt) as IntermediateCatchEventImpl;
        flowNode.Id = "IntermediateCatchEvent" + BpmnModelHelper.GenerateElementId();
        // 创建而非复制时执行
        if (flowElt == null) {
            flowNode.initEventDefinitions();
        }

        return flowNode;
    }

    bindBpmnModel(flowElt?: IntermediateCatchEventImpl, diagElt?: BPMNShape): void {
        super.bindBpmnModel(flowElt, diagElt);

        const bpmnModel = this.editorUi.graph.bpmnModel;
        this.addSignals(this.flowElement);
    }

    addSignals(catchEvent: IntermediateCatchEventImpl) {
        const bpmnModel = catchEvent.Model;
        if (catchEvent.EventDefinitions && catchEvent.EventDefinitions.length > 0) {
            if (!bpmnModel.signals) {
                bpmnModel.signals = [];
            }
            for (const e of catchEvent.EventDefinitions) {
                const event = e as SignalEventDefinitionImpl;
                if (bpmnModel.signals.findIndex(s => s.Name === event.SignalRef) === -1) {
                    const signal = new Signal(bpmnModel);
                    signal.Name = event.SignalRef;
                    bpmnModel.signals.push(signal);
                }
            }
        }
    }

    getFarrisPropConfig(): FarrisPropConfigWithData {
        let act = this.flowElement;

        let basicProperty: ElementPropertyConfig = new ElementPropertyConfig();
        let activityProperty: ElementPropertyConfig = new ElementPropertyConfig();
        let propertyData: any = {};

        basicProperty.categoryId = 'basicProperty';
        basicProperty.categoryName = mxResources.get('basicInformation');
        basicProperty.tabId = 'basic';
        basicProperty.tabName = mxResources.get('basicAttributes');
        basicProperty.properties = [
            { propertyID: IntermediateCatchEventImplPropertyKeys.Name, propertyName: mxResources.get('name'), propertyType: 'string', visible: !this.editorUi.multiLangEnabled },
            { propertyID: IntermediateCatchEventImplPropertyKeys.NameLanguage, propertyName: mxResources.get('nameLanguage'), propertyType: 'multiLanguage', visible: this.editorUi.multiLangEnabled },
            { propertyID: IntermediateCatchEventImplPropertyKeys.Id, propertyName: mxResources.get('id'), propertyType: 'string', readonly: true }
        ];


        activityProperty.categoryId = 'activityProperty';
        activityProperty.categoryName = mxResources.get('activityProperty');
        activityProperty.tabId = 'basic';
        activityProperty.tabName = mxResources.get('basicAttributes');
        activityProperty.properties = [
            { propertyID: IntermediateCatchEventImplPropertyKeys.EventDefinitions, propertyName: mxResources.get('eventDefinitions'), propertyType: 'modal' },
            { propertyID: IntermediateCatchEventImplPropertyKeys.variableSettings, propertyName: mxResources.get('variableSettings'), propertyType: 'modal' }
        ];
        if (act.EventDefinitions && this.isBackEventExist(act.EventDefinitions)) {
            activityProperty.properties.push(
                { propertyID: IntermediateCatchEventImplPropertyKeys.BackWardRule, propertyName: mxResources.get('backWardRule'), propertyType: 'modal' }
            );
            propertyData[IntermediateCatchEventImplPropertyKeys.BackWardRule] = act.backWardRule;
        }

        propertyData[IntermediateCatchEventImplPropertyKeys.Id] = act.Id;
        propertyData[IntermediateCatchEventImplPropertyKeys.ClrTypeID] = act.ClrTypeID;
        propertyData[IntermediateCatchEventImplPropertyKeys.Name] = act.Name;
        propertyData[IntermediateCatchEventImplPropertyKeys.NameLanguage] = act.NameLanguage || {
            "zh-CHS": act.Name,
        };
        propertyData[IntermediateCatchEventImplPropertyKeys.EventDefinitions] = act.EventDefinitions;
        propertyData[IntermediateCatchEventImplPropertyKeys.variableSettings] = PropertyHelper.getVariableSettings(act.Model, act.variableSettings);

        return {
            propertyConfig: [basicProperty, activityProperty],
            propertyData: propertyData
        }
    }

    /**
     * 判断是否存在反向事件
     * @param
     */
    isBackEventExist(events: EventDefinition[]) {
        for (let e of events) {
            const event = e as SignalEventDefinitionImpl;
            if (event.triggerAction === 'Back') {
                return true;
            }
        }
        return false;
    }

    updateProps(obj: any) {
        const catchEvent = this.flowElement;
        const propertyId: string = obj.propertyID;
        switch (propertyId) {
            case IntermediateCatchEventImplPropertyKeys.EventDefinitions:
                this.updateEventDefinitions(catchEvent, obj);
                break;
            case IntermediateCatchEventImplPropertyKeys.BackWardRule:
                this.updateBackWardRule(catchEvent, obj);
                break;
            case IntermediateCatchEventImplPropertyKeys.variableSettings:
                this.updateVariableSettings(catchEvent, obj);
                break;
            default:
                super.updateProps(obj);
                break;
        }
    }

    updateEventDefinitions(catchEvent: IntermediateCatchEventImpl, obj: any) {
        if (catchEvent) {
            this.removeSignals(catchEvent);
            let events = obj.propertyValue as any[];
            catchEvent.EventDefinitions = new Array<EventDefinition>();
            for (let e of events) {
                let item = new SignalEventDefinitionImpl(catchEvent.Model);
                item.name = e.name;
                item.triggerAction = e.triggerAction
                item.Async = false;
                item.SignalRef = e.SignalRef;

                catchEvent.EventDefinitions.push(item);
            }
            this.addSignals(catchEvent);
        }
        // 判断审批项是否包含驳回？不包含时，驳回规则清空
        if (!this.isBackEventExist(catchEvent.EventDefinitions)) {
            catchEvent.backWardRule = null;
        } else if (!catchEvent.backWardRule) {
            catchEvent.backWardRule = new BackWardRule(catchEvent.Model);
        }
        // 更新属性框
        // Utils.postMessage(ResourceKeys.wf_showProperty, this.getPropConfig());
    }

    updateBackWardRule(catchEvent: IntermediateCatchEventImpl, obj: any) {
        const data = obj.propertyValue;
        let backWardRule = new BackWardRule(catchEvent.Model);
        backWardRule.TargetRef = data.TargetRef;
        backWardRule.SelectTargetAble = data.SelectTargetAble;
        backWardRule.WaitReturn = data.WaitReturn;
        backWardRule.ResubmitEffect = data.ResubmitEffect;
        backWardRule.DynamicBackWardRuleAble = data.DynamicBackWardRuleAble;
        backWardRule.SelectTargetList = data.SelectTargetList;

        catchEvent.backWardRule = backWardRule;
    }

    updateVariableSettings(catchEvent: IntermediateCatchEventImpl, obj: any) {
        const bpmnModel = catchEvent.Model;
        const extendElements = bpmnModel.DefaultProcess.GetExtensionElements();
        if (catchEvent.variableSettings && catchEvent.variableSettings.length > 0) {
            for (const v of catchEvent.variableSettings) {
                if (v.tag === 'component') {
                    const i = extendElements.findIndex(e => (e instanceof Component && e.Id === v.value));
                    extendElements.splice(i, 1);
                }
            }
        }
        catchEvent.variableSettings = new Array();

        const variables = obj.propertyValue as any[];
        if (variables && variables.length > 0) {
            for (const v of variables) {
                const variableSetting = new VariableSetting(bpmnModel);
                // variableSetting.variableId = v.variableId;
                variableSetting.variableCode = v.variableCode;
                variableSetting.variableName = v.variableName;
                variableSetting.tag = v.tag;
                variableSetting.executionTime = v.executionTime;
                variableSetting.value = v.value;
                variableSetting.valueText = v.valueText;

                catchEvent.variableSettings.push(variableSetting);
                if (variableSetting.tag === 'component') {
                    const c = v.component;
                    if (c) {
                        let cpt = new Component(bpmnModel);
                        cpt.MetadataId = c.MetadataId;
                        cpt.MethodCode = c.MethodCode;
                        cpt.Id = c.Id;
                        cpt.Name = c.Name;
                        cpt.ActualParameters = new Array();
                        if (c.ActualParameters && c.ActualParameters.length > 0) {
                            for (let a of c.ActualParameters) {
                                let parameter = new ActualParameter(bpmnModel, a.Name, a.Value);
                                parameter.Tag = a.Tag;
                                cpt.ActualParameters.push(parameter);
                            }
                        }

                        extendElements.push(cpt);
                    }
                }
            }
        }
    }

    removeSignals(catchEvent: IntermediateCatchEventImpl) {
        const bpmnModel = catchEvent.Model;
        if (catchEvent.EventDefinitions && catchEvent.EventDefinitions.length > 0) {
            const flowElements = bpmnModel.DefaultProcess.GetFlowElements();
            const eventDefs = new Map<string, SignalEventDefinitionImpl>();
            for (const key in flowElements) {
                if (flowElements[key] instanceof IntermediateCatchEventImpl && flowElements[key].Id !== catchEvent.Id) {
                    const catchEvent = flowElements[key] as IntermediateCatchEventImpl;
                    for (const e of catchEvent.EventDefinitions) {
                        const event = e as SignalEventDefinitionImpl;
                        if (!eventDefs.get(event.SignalRef)) {
                            eventDefs.set(event.SignalRef, event);
                        }
                    }
                }
            }
            for (const e of catchEvent.EventDefinitions) {
                const eventDef = e as SignalEventDefinitionImpl;
                if (!eventDefs.get(eventDef.SignalRef)) {
                    const i = bpmnModel.signals.findIndex(s => s.Name === eventDef.SignalRef);
                    if (i > -1) {
                        bpmnModel.signals.splice(i, 1);
                    }
                }
            }
        }
    }
}
